/**
 * DataTable Component
 * 
 * A reusable, customizable data table component built on top of PrimeReact DataTable.
 * Provides advanced features like pagination, sorting, row expansion, and dynamic column rendering.
 * 
 * Features:
 * - Dynamic column generation from configuration objects
 * - Built-in pagination with custom templates
 * - Sortable columns with custom sort icons
 * - Row expansion for sub-table functionality
 * - Customizable styling through styled-components
 * - Touch/pointer event handling for mobile devices
 * - Lazy loading support
 * - Empty state messaging
 * - Responsive design with scrollable content
 * 
 * Dependencies:
 * - PrimeReact DataTable and Column components
 * - Custom styled-components for theming
 * - usePaginatorTemplate hook for pagination logic
 * - RowExpansionTemplate for expandable rows
 * 
 * @param {Object} props - Component props
 * @param {Array} props.columns - Array of column configuration objects
 * @param {Array} props.data - Array of data objects to display
 * @param {number} props.totalRecords - Total number of records for pagination
 * @param {Function} props.onPageChange - Callback for page change events
 * @param {boolean} props.paginator - Enable/disable pagination
 * @param {number} props.first - Index of first record to display
 * @param {number} props.rows - Number of rows per page
 * @param {Function} props.onSort - Callback for sort events
 * @param {string} props.sortField - Current sort field
 * @param {number} props.sortOrder - Current sort order (1 or -1)
 * @param {string} props.scrollHeight - Height of scrollable area
 * @param {Function} props.onRowClick - Callback for row click events
 * @param {boolean} props.showSubTable - Enable row expansion functionality
 * @param {Array} props.subColDatas - Column configuration for sub-tables
 * @param {string} props.emptyMessage - Message to display when no data
 * @param {string} props.columnHeight - Default height for columns
 * @param {Object} props.expandRow - Configuration for expanded rows
 * 
 * @returns {JSX.Element} Rendered DataTable component
 * 
 * @example
 * const columns = [
 *   { field: 'name', header: 'Name', sortable: true, width: '200px' },
 *   { field: 'email', header: 'Email', sortable: false, width: '250px' }
 * ];
 * 
 * const data = [
 *   { name: 'John Doe', email: 'john@example.com' },
 *   { name: 'Jane Smith', email: 'jane@example.com' }
 * ];
 * 
 * <DataTable 
 *   columns={columns}
 *   data={data}
 *   totalRecords={2}
 *   paginator={true}
 *   rows={10}
 *   onPageChange={handlePageChange}
 *   onSort={handleSort}
 * /> 

/* eslint-disable react/display-name */
import React from 'react';
import { DataTable } from 'primereact/datatable'; 
import SortTableAscending from 'src/common/refactoredComponents/assets/images/SortTableAscending.svg';
import SortTableDescending from 'src/common/refactoredComponents/assets/images/SortTableDescending.svg';
import { DataTableContainer} from './DataTable.styled'; 
import usePaginatorTemplate from './hooks/usePaginatorTemplate'; 
import { Column } from 'primereact/column';
import RowExpansionTemplate from './RowExpansionTemplateContainer';
import {SMALL_TEXT_PX} from '../../../styles/fonts/MCFontConstants';

const Datatable = (props) => {
    const {
        columns,
        data,
        totalRecords = 0,
        onPageChange,
        paginator,
        first,
        rows,
        onSort,
        sortField,
        sortOrder,
        scrollHeight,
        onRowClick,
        isListView,
        sortMargin,
        showScrollBar = true,
        displayType,
        subColDatas,
        showSubTable,
        keyValue,
        bundleSubGroup,
        expandRow,
        headerBorderBottom,
        backgroundColorPagination,
        onResumeScrolling = () => {},
        onHideScrolling = () => {},
        subTableWidth,
        emptyMessage,
        columnHeight='42px',
        defaultScrollHeight='300px',
        defaultScrollBarWidth='5px',
        defaultRows=10,
        padding,
        border,
        backgroundColorOdd,
        backgroundColorEven,
        height,
        ipadNoMarginTop,
        marginTop,
        paginationLeftMarginBottom,
        rightCOntentMarginTop,
        id,
        isOrderHistory
    } = props;

    const { paginatorLeftTemplate, paginatorTemplate } = usePaginatorTemplate({
        totalRecords,
        first,
        rows,
        paginationLeftMarginBottom,
        rightCOntentMarginTop
    });

     const dynamicSubTable = subColDatas?.map((col) => {
            return (
                <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    sortable={col.sortable}
                    style={{
                        width: col.width,
                        height: '35px',
                        paddingLeft: col.paddingLeft,
                        padding: col.padding,
                        display: col.display,
                    }}
                    {...col}
                />
            );
        });

    // Fix: rowExpansionTemplate must be a function that receives rowData
    const rowExpansionTemplateContianer = (data) => 
        {return (
         <div className="data-subtable">
            <DataTable
                value={data[bundleSubGroup]}
                header={null}
                responsiveLayout="scroll"
            >
                {dynamicSubTable}
            </DataTable>
        </div>
        );
        };
  
    const renderDynamicColumns = () => {
        if (!columns || !Array.isArray(columns)) {
            return null;
        }

        return columns.map((col) => (
            <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={col.sortable}
                style={{
                    width: col.width,
                    height: columnHeight,
                    paddingLeft: col.paddingLeft,
                    padding: col.padding,
                }}
                {...col}
            />
        ));
    };

    return (
        <div
            onPointerMove={onResumeScrolling}
            onTouchMove={onResumeScrolling}
            // onTouchEnd={onHideScrolling}
        >
            <DataTableContainer
                sortDescIcon={SortTableDescending}
                sortAscIcon={SortTableAscending}
                border={border}
                backgroundColorOdd={backgroundColorOdd}
                backgroundColorEven={backgroundColorEven}
                backgroundColorPagination={backgroundColorPagination}
                isListView={isListView}
                isOrderHistory={isOrderHistory}
                sortMargin={sortMargin}
                height={height}
                showScrollBar={showScrollBar}
                headerBorderBottom={headerBorderBottom}
                displayType={displayType}
                showSubTable={showSubTable}
                subTableWidth={subTableWidth}
                scrollBarWidth={defaultScrollBarWidth}
                padding={padding}
                ipadNoMarginTop={ipadNoMarginTop}
                marginTop={marginTop}
            >
                <DataTable 
                    autoLayout
                    id={id}
                    stripedRows
                    value={data}
                    className="p-datatable-responsive"
                    scrollable
                    scrollHeight={scrollHeight ? scrollHeight : defaultScrollHeight}
                    bodyStyle={{ fontSize: SMALL_TEXT_PX }}
                    paginator={paginator}
                    rows={rows ? rows : defaultRows}
                    paginatorLeft={paginatorLeftTemplate}
                    paginatorTemplate={paginatorTemplate}
                    totalRecords={totalRecords}
                    first={first}
                    onPage={onPageChange}
                    onSort={onSort}
                    pageLinkSize={5}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onRowClick={onRowClick}
                    lazy
                    rowExpansionTemplate={
                        showSubTable ? rowExpansionTemplateContianer : null
                    }
                    expandedRows={showSubTable ? expandRow : null}
                    dataKey={keyValue ? keyValue : null}
                    emptyMessage={emptyMessage ? emptyMessage : null}
                > 
                    {renderDynamicColumns()}
                </DataTable>
            </DataTableContainer>
        </div>
    );
};

export default Datatable;
